import React, { useEffect, useState } from "react";
import { FaEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { OvertimePermission } from "../types";
import {
  getEmployeeOvertimePermissions,
  createOvertimePermission,
  updateOvertimePermission,
  deleteOvertimePermission,
} from "../services/apiService";
import ActionButton from "./ActionButton";
import "./OvertimePermissionsManager.css";

interface OvertimePermissionsManagerProps {
  employeeUserId: string;
  employeeName: string;
  onDataChange?: () => void;
  refreshTrigger?: number;
}

const OvertimePermissionsManager: React.FC<OvertimePermissionsManagerProps> = ({
  employeeUserId,
  employeeName,
  onDataChange,
  refreshTrigger,
}) => {
  const [permissions, setPermissions] = useState<OvertimePermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  // Form state
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [hasEndDate, setHasEndDate] = useState(true);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const data = await getEmployeeOvertimePermissions(employeeUserId);
      setPermissions(data);
      setError(null);
    } catch (err: any) {
      console.error("Error fetching overtime permissions:", err);
      setError("Fehler beim Laden der Überstundengenehmigungen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeUserId]);

  // Refresh when refreshTrigger changes (external permission added)
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchPermissions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const resetForm = () => {
    setStartDate("");
    setEndDate("");
    setHasEndDate(true);
    setEditingId(null);
    setIsAdding(false);
  };

  const handleAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    resetForm();
  };

  const handleEdit = (permission: OvertimePermission) => {
    setEditingId(permission.id);
    setIsAdding(false);
    setStartDate(permission.startDate);
    setEndDate(permission.endDate || "");
    setHasEndDate(!!permission.endDate);
  };

  const handleSave = async () => {
    if (!startDate) {
      alert("Startdatum ist erforderlich");
      return;
    }

    try {
      if (isAdding) {
        await createOvertimePermission(
          employeeUserId,
          startDate,
          hasEndDate ? endDate : null
        );
      } else if (editingId !== null) {
        await updateOvertimePermission(
          editingId,
          startDate,
          hasEndDate ? endDate : null
        );
      }
      await fetchPermissions();
      onDataChange?.();
      resetForm();
    } catch (err: any) {
      console.error("Error saving overtime permission:", err);
      alert("Fehler beim Speichern der Überstundengenehmigung");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Möchten Sie diese Überstundengenehmigung wirklich löschen?")) {
      return;
    }

    try {
      await deleteOvertimePermission(id);
      await fetchPermissions();
      onDataChange?.();
    } catch (err: any) {
      console.error("Error deleting overtime permission:", err);
      alert("Fehler beim Löschen der Überstundengenehmigung");
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("de-DE");
  };

  if (loading) {
    return <div className="loading">Überstundengenehmigungen werden geladen...</div>;
  }

  return (
    <>
      <div className="section-header">
        <h2>Überstundengenehmigungen</h2>
        {!isAdding && !editingId && (
          <ActionButton onClick={handleAdd}>
            + Neue Genehmigung
          </ActionButton>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {(isAdding || editingId !== null) && (
        <div className="permission-form">
          <h3>{isAdding ? "Neue Genehmigung" : "Genehmigung bearbeiten"}</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startDate">Startdatum *</label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={hasEndDate}
                  onChange={(e) => setHasEndDate(e.target.checked)}
                />
                Enddatum festlegen
              </label>
            </div>

            {hasEndDate && (
              <div className="form-group">
                <label htmlFor="endDate">Enddatum</label>
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                />
              </div>
            )}
          </div>

          <div className="form-actions">
            <ActionButton onClick={handleSave}>
              Speichern
            </ActionButton>
            <ActionButton onClick={resetForm} variant="secondary">
              Abbrechen
            </ActionButton>
          </div>
        </div>
      )}

      {permissions.length === 0 ? (
        <p className="no-permissions">
          Keine Überstundengenehmigungen für {employeeName} vorhanden.
        </p>
      ) : (
        <div className="table-wrapper">
          <table className="history-table">
            <thead>
              <tr>
                <th>Startdatum</th>
                <th>Enddatum</th>
                <th className="actions">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {permissions
                .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
                .map((permission) => (
                  <tr key={permission.id}>
                    <td>{formatDate(permission.startDate)}</td>
                    <td>
                      {permission.endDate ? formatDate(permission.endDate) : "Unbegrenzt"}
                    </td>
                    <td className="actions">
                      <button
                        onClick={() => handleEdit(permission)}
                        className="delete-icon-button"
                        disabled={isAdding || editingId !== null}
                        title="Bearbeiten"
                        style={{ backgroundColor: "var(--secondary-color)" }}
                      >
                        {FaEdit({})}
                      </button>
                      <button
                        onClick={() => handleDelete(permission.id)}
                        className="delete-icon-button"
                        disabled={isAdding || editingId !== null}
                        title="Löschen"
                      >
                        {MdDelete({})}
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default OvertimePermissionsManager;
